import React, { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Monitor, Moon, Sun } from "lucide-react";
import type { ThemeMode } from "../../contexts/DarkModeContext";
import { useDarkModeContext } from "../../contexts/DarkModeContext";

const MODES: { value: ThemeMode; Icon: React.ElementType }[] = [
  { value: "system", Icon: Monitor },
  { value: "light", Icon: Sun },
  { value: "dark", Icon: Moon },
];

export function ThemeIconDropdown() {
  const { t } = useTranslation();
  const { themeMode, setThemeMode } = useDarkModeContext();
  const [open, setOpen] = useState(false);
  const [pinned, setPinned] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
        setPinned(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  const labels: Record<ThemeMode, string> = {
    system: t("common.themeSystem"),
    light: t("common.themeLight"),
    dark: t("common.themeDark"),
  };

  const CurrentIcon = MODES.find((m) => m.value === themeMode)?.Icon ?? Monitor;

  return (
    <div
      ref={containerRef}
      className="relative flex items-center justify-center"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => { if (!pinned) setOpen(false); }}
    >
      <button
        type="button"
        onClick={() => { setPinned(true); setOpen(true); }}
        className="flex h-10 w-10 items-center justify-center rounded-full text-gray-600 dark:text-slate-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-white/10 transition-colors"
        aria-label={t("common.appearance")}
        aria-expanded={open}
        aria-haspopup="true"
      >
        <CurrentIcon size={20} strokeWidth={1.5} />
      </button>
      <div
        className={`absolute right-0 top-full mt-1.5 min-w-[10rem] rounded-xl border border-gray-200/80 dark:border-white/10 bg-white dark:bg-slate-800/95 shadow-lg overflow-hidden transition-all duration-200 ${
          open ? "opacity-100 visible" : "opacity-0 invisible pointer-events-none"
        }`}
        role="menu"
      >
        {MODES.map(({ value, Icon }) => (
          <button
            key={value}
            type="button"
            role="menuitem"
            onClick={() => {
              setThemeMode(value);
              setOpen(false);
              setPinned(false);
            }}
            className={`w-full flex items-center gap-2.5 px-4 py-2.5 text-left text-sm font-medium transition-colors ${
              themeMode === value
                ? "bg-gray-100 dark:bg-white/10 text-gray-900 dark:text-white"
                : "text-gray-600 dark:text-slate-400 hover:bg-gray-50 dark:hover:bg-white/5 hover:text-gray-900 dark:hover:text-white"
            }`}
          >
            <Icon size={16} strokeWidth={1.5} className="shrink-0" />
            {labels[value]}
          </button>
        ))}
      </div>
    </div>
  );
}
